import type { StyleProp, ViewStyle } from 'react-native'
import { StyleSheet, View } from 'react-native'
import { Chip } from 'react-native-paper'
import { hexToRgba } from '@/lib/color-utils'
import type { Tag as TagModel } from '@/types/locations'

interface TagProps {
    tag: Pick<TagModel, 'id' | 'name' | 'color'>
    style?: StyleProp<ViewStyle>
    onPress?: () => void
    // 'outlined' (default): white bordered pill with a colored dot swatch.
    // 'filled': solid color-tinted chip with colored text — matches the job
    // status badge so both read as the same kind of element when shown together.
    variant?: 'outlined' | 'filled'
    // Shrinks the (filled) chip's height/text to sit inline on a small text line.
    compact?: boolean
}

const FALLBACK_COLOR = '#6B7280'

export function Tag({ tag, style, onPress, variant = 'outlined', compact = false }: TagProps) {
    const tagColor = tag.color && tag.color.trim().length > 0 ? tag.color : FALLBACK_COLOR

    if (variant === 'filled') {
        return (
            <Chip
                mode="flat"
                compact={compact}
                onPress={onPress}
                style={[
                    styles.filledChip,
                    compact && styles.filledChipCompact,
                    { backgroundColor: hexToRgba(tagColor, 0.15) },
                    style,
                ]}
                textStyle={[
                    styles.filledText,
                    compact && styles.filledTextCompact,
                    { color: tagColor },
                ]}
            >
                {tag.name}
            </Chip>
        )
    }

    return (
        <Chip
            mode="outlined"
            onPress={onPress}
            icon={() => <View style={[styles.colorSwatch, { backgroundColor: tagColor }]} />}
            style={[styles.chip, style]}
            textStyle={styles.text}
        >
            {tag.name}
        </Chip>
    )
}

const styles = StyleSheet.create({
    chip: {
        borderRadius: 16,
        borderColor: '#d1d5db',
        backgroundColor: '#ffffff',
        marginRight: 8,
        marginBottom: 8,
    },
    text: {
        color: '#111827',
        fontWeight: '500',
    },
    filledChip: {
        borderRadius: 4,
        marginRight: 8,
        marginBottom: 8,
    },
    filledText: {
        fontWeight: '600',
        fontSize: 12,
    },
    filledChipCompact: {
        // Tighten the chip so it lines up with a 12px text line beside it.
        height: 22,
        alignItems: 'center',
        justifyContent: 'center',
    },
    filledTextCompact: {
        fontSize: 11,
        lineHeight: 14,
        marginVertical: 0,
    },
    colorSwatch: {
        width: 12,
        height: 12,
        borderRadius: 6,
        marginRight: 4,
    },
})
